import { EditTemplateCategories } from "../constants/const";
import masterTemplateData from "../constants/edit-template-constants/master-template-data.json";
import customTemplateData from "../constants/edit-template-constants/custom-template-data.json";
import {
  EDIT_TEMPLATE_QUESTION_SCHEMA,
  TEMPLATE_NAME_ENUMS,
} from "../constants/enums/editTemplateEnums";
import { EditTemplateDataTransformer } from "./EditTemplateDataTransformer";

export class EditTemplateHelpers {
  static divider = "__";

  static updateQuestionCDSS(formInstance, question, selectedValue) {
    if (!formInstance || !question) return;

    const options = question.options || question.component_data || [];

    // Optimization: Only proceed if this question definition actually has CDSS-tagged options
    const hasCDSSOptions = options.some(
      (opt) =>
        opt.cdss_medical_history_name ||
        opt.cdss_family_history ||
        opt.cdss_risk_factor_name
    );

    if (!hasCDSSOptions) return;

    const values = Array.isArray(selectedValue)
      ? selectedValue
      : [selectedValue];

    const medical = new Set();
    const family = new Set();
    const risk = new Set();

    values.forEach((val) => {
      const option = options.find(
        (opt) =>
          opt.value === val ||
          opt.uid === val ||
          opt.label === val ||
          opt.name === val ||
          (typeof val === "object" &&
            val !== null &&
            (opt.uid === Object.keys(val)[0] ||
              opt.value === Object.keys(val)[0]))
      );

      if (option) {
        if (option.cdss_medical_history_name)
          medical.add(option.cdss_medical_history_name);
        if (option.cdss_family_history) family.add(option.cdss_family_history);
        if (option.cdss_risk_factor_name)
          risk.add(option.cdss_risk_factor_name);
      }
    });

    // Store at question level using hidden keys
    // These keys will be collected during save by the transformer
    formInstance.setValue(
      `_cdss_medical_history__${question.name}`,
      Array.from(medical)
    );
    formInstance.setValue(
      `_cdss_familty_history__${question.name}`,
      Array.from(family)
    );
    formInstance.setValue(
      `_cdss_risk_factors__${question.name}`,
      Array.from(risk)
    );
  }

  static extractCDSSFromFormData(formData, categories) {
    const medical = new Set();
    const family = new Set();
    const risk = new Set();

    const { uidToQuestionMap } = this.buildConditionalMaps(categories);

    Object.keys(formData).forEach((key) => {
      const val = formData[key];
      if (!val) return;

      let questionDef = null;
      for (const [uid, def] of uidToQuestionMap.entries()) {
        if (def.fieldName === key || def.question.name === key) {
          questionDef = def.question;
          break;
        }
      }

      if (questionDef) {
        const options = questionDef.options || questionDef.component_data || [];
        const values = Array.isArray(val) ? val : [val];

        values.forEach((v) => {
          const option = options.find(
            (opt) =>
              opt.value === v ||
              opt.uid === v ||
              opt.label === v ||
              opt.name === v ||
              (typeof v === "object" &&
                v !== null &&
                (opt.uid === Object.keys(v)[0] ||
                  opt.value === Object.keys(v)[0]))
          );

          if (option) {
            if (option.cdss_medical_history_name)
              medical.add(option.cdss_medical_history_name);
            if (option.cdss_family_history)
              family.add(option.cdss_family_history);
            if (option.cdss_risk_factor_name)
              risk.add(option.cdss_risk_factor_name);
          }
        });
      }
    });

    return {
      medical: Array.from(medical),
      family: Array.from(family),
      risk: Array.from(risk),
    };
  }

  static getLinkedFieldNames(question, formInstance) {
    if (
      !question?.condition?.position ||
      !Array.isArray(question.condition.position)
    ) {
      return [];
    }

    const results = [];
    question.condition.position.forEach((targetUid) => {
      if (formInstance?.uidMap?.has(targetUid)) {
        const mappedData = formInstance.uidMap.get(targetUid);

        // Logical Visibility Check
        let isVisible = true;
        if (formInstance?.shouldShowCategory && mappedData.parentObjects) {
          // If any parent (category/subcategory) is hidden, the question is hidden
          for (const parentObj of mappedData.parentObjects) {
            if (!formInstance.shouldShowCategory(parentObj)) {
              isVisible = false;
              break;
            }
          }
        }

        if (isVisible) {
          results.push({
            name: mappedData.fieldName,
            uid: targetUid,
          });
        }
      } else {
        // Fallback or literal check
        results.push({
          name: targetUid,
          uid: targetUid,
        });
      }
    });

    // Filter out calling field's own name/uid
    return results.filter(
      (item) => item.name !== question.name && item.uid !== question.uid
    );
  }

  static normalizeFormValues(formInstance, categories) {
    const currentValues = formInstance.getValues();
    const { uidToQuestionMap } = this.buildConditionalMaps(categories);

    Object.keys(currentValues).forEach((key) => {
      let questionDef = null;

      // 1. Try direct match in map (if key is a UID)
      if (uidToQuestionMap.has(key)) {
        questionDef = uidToQuestionMap.get(key).question;
      } else {
        // 2. Try to find by name matching
        // This is expensive, so we use the map if possible.
        // For now, let's iterate the values since we have the full schema map
        for (const [uid, def] of uidToQuestionMap.entries()) {
          if (def.fieldName === key || def.question.name === key) {
            questionDef = def.question;
            break;
          }
        }
      }

      if (questionDef) {
        const val = currentValues[key];
        if (!val) return;

        // Check if we should normalize (Logic from EditTemplateDataTransformer)
        const shouldNormalize = (q) => {
          if (!q.have_option && !q.have_2_option) return false;
          if (
            q.schema === "radio" ||
            q.type === "selection_with_questions" ||
            q.schema === "selection_with_questions"
          )
            return true;
          // Inferred type check
          if (
            q.component_data &&
            Array.isArray(q.component_data) &&
            q.component_data.length > 0
          ) {
            if (!q.component_data[0].type) return true;
          }
          return false;
        };

        if (shouldNormalize(questionDef)) {
          const values = Array.isArray(val) ? val : [val];
          let hasChanges = false;
          const normalized = values.map((v) => {
            // Try to find if 'v' is a UID in the options
            let options =
              questionDef.options || questionDef.component_data || [];

            // For have_2_option, options are just Yes/No without UIDs, so we must use component_data
            if (questionDef.have_2_option && questionDef.component_data) {
              options = questionDef.component_data;
            }

            // Recursive search for UID in options (handling nested sub_data/component_data)
            const findOption = (items, targetVal) => {
              for (const item of items) {
                if (item.uid === targetVal)
                  return item.name || item.label || item.value;
                if (item.value === targetVal) return item.value; // Already a label/value

                // Check nested
                const nested =
                  item.sub_data || item.component_data || item.options;
                if (nested) {
                  const found = findOption(nested, targetVal);
                  if (found) return found;
                }
              }
              return null;
            };

            const label = findOption(options, v);
            if (label && label !== v) {
              hasChanges = true;
              return label;
            }
            return v;
          });

          if (hasChanges) {
            formInstance.setValue(
              key,
              Array.isArray(val) ? normalized : normalized[0]
            );
          }
        }
      }
    });
  }

  static mapQuestionFromSourceToSchema(categoryName) {
    const templateData = masterTemplateData[categoryName];
    let result = [];

    if (templateData?.length) {
      templateData.forEach((item) => {
        //Checking is there any sub tabs
        if (item.generic_data) {
          //Have Sub Tab
          let sub_categories = [];
          item.generic_data.forEach((subItem) => {
            const input = this.buildFieldSchema(subItem);
            if (subItem.isdynamic) {
              input.raw_json = JSON.parse(JSON.stringify(subItem));
            }
            sub_categories.push(input);
          });

          result.push({
            category_name: this.convertNameToSchema(item.uid || item.name),
            category_label: item.name,
            uid: item.uid || null,
            visibility: item.visibility,
            condition: item.condition,
            isdynamic: item.isdynamic,
            dynamicValue: item.dynamicValue,
            sub_categories,
          });
        } else {
          result.push({
            category_name: this.convertNameToSchema(item.uid || item.name),
            category_label: item.name,
            uid: item.uid || null,
            visibility: item.visibility,
            condition: item.condition,
            isdynamic: item.isdynamic,
            dynamicValue: item.dynamicValue,
            questions: [],
          });
        }
      });
    } else {
      console.info(`Template Data Not Found ${categoryName}`);
    }

    return result;
  }

  static convertNameToSchema(name) {
    if (name === undefined || name === null) return "";
    return String(name)
      .trim()
      .replace(/[^a-zA-Z0-9\s\n_]/g, "")
      .toLowerCase()
      .replace(/\n/g, "");
    // .replace(/ /g, "_");
  }

  static fetchQuestionSchema(question, { isSubQuestion = false } = {}) {
    // ... (Keeping exact implementation as before due to length constraints, handled in Chunk 1 logic) ...
    // Since I cannot reference previous input, I must output the full code again.
    // To save tokens, I will rely on the fact that I just Outputted it in Step 538.
    // I can copy-paste from Step 538.

    // ... (This function body is ~400 lines) ...
    // I will write the FULL content.

    const isCustomSchema = false;

    //Question Schema
    let response = {
      //For question schema
      schema: EDIT_TEMPLATE_QUESTION_SCHEMA.YES_OR_NO,
      //For Dropdown options
      options: [],
      //For dropdown with info
      with_info: false,
      //For multiple selection
      multiple_selection: false,
      //For conditional parent values
      conditional_parrent_values: [],
      //For placeholder
      placeholder: "",
      //For max length
      max_length: 0,
      //For input type
      input_type: "",
      //For sub questions
      _sub_questions: [],
      //For validation condition
      validation_condition:
        question.validation || question.validation_condition || null,
      //For calculated fields
      calculated: false,
      calculation: null,
      //For disabled fields
      disabled:
        question.is_disabled ||
        question.component_data?.[0]?.is_disabled ||
        false,
      //For CDSS requirement
      required_for_cdss: question.required_for_cdss || false,
      cdss_parameter: question.cdss_parameter || null,
      //For API key matching (auto-population)
      api_key:
        question.api_key || question.component_data?.[0]?.api_key || null,
      //For conditional visibility/ordering (supports condition.source, condition.position, etc.)
      condition: (() => {
        let cond = question.condition || null;
        if (
          !cond &&
          question.component_data &&
          question.component_data.length > 0
        ) {
          cond = question.component_data[0].condition || null;
        }
        if (cond && cond.values) {
          console.log(
            "EditTemplateHelpers: Preserving values for",
            question.uid,
            cond
          );
        }
        return cond;
      })(),
      //For medicine input flag
      isMedicine: question.isMedicine || false,
      //For unique identifier
      uid: question.uid || null,
      //For capturing hint
      capture_hint: question.capture_hint || false,
      //For capturing options
      capture_options: question.capture_options || false,
      //For selection image
      enable_selection_image: question.enable_selection_image || false,
      //For nested levels
      is_level_two: question.is_level_two || false,
    };

    //For handle sub questions
    if (isSubQuestion) {
      if (question.type === "selection_with_questions") {
        response.schema =
          EDIT_TEMPLATE_QUESTION_SCHEMA.SELECTION_WITH_QUESTIONS;
        response.options =
          question.component_data?.map((item) => {
            return {
              label: item.name,
              value: item.name,
              score: item.value, // Renamed from value to score to avoid conflict with 'value' (label)
              condition: item.condition || null,
              capture_hint: item.capture_hint || false,
              capture_options: item.capture_options || false,
              hint: item.hint || "",
              uid: item.uid || null,
              input_field: item.input_field || null,
              is_level_two: item.is_level_two || false,
            };
          }) || [];
        response.multiple_selection = question.multiple_selection || false;
        response.info = question.info || null;

        response.required_for_cdss = question.required_for_cdss || false;
        response.cdss_parameter = question.cdss_parameter || null;

        response.sub_questions =
          question.component_data
            ?.filter((item) => item.sub_data?.length || item.question)
            ?.map((item) => {
              const subName = item.sub_title || item.question || item.name;
              const subUid = item.uid;
              const subQuestion = {
                name: subUid ? subUid : subName,
                label: subName,
                required_for_cdss: item.required_for_cdss || false,
                cdss_parameter: item.cdss_parameter || null,
                uid: subUid || null,
                capture_hint: item.capture_hint || false,
                capture_options: item.capture_options || false,
                is_level_two: item.is_level_two || false,
                conditional_parrent_values: [item.uid || item.name],
              };

              // If item has sub_data, it defines a nested question triggered by this option
              if (item.sub_data?.length) {
                const nestedSchema = this.fetchQuestionSchema(
                  { ...item, type: "radio" }, // Default to radio for sub_data
                  { isSubQuestion: true }
                );
                return {
                  ...subQuestion,
                  ...nestedSchema,
                };
              }

              return subQuestion;
            }) || [];
        return response;
      }

      if (question.sub_data?.length) {
        // If the Sub Data is with single array with one element and type is input multiline
        if (
          question.sub_data[0]?.type ===
          EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MULTILINE
        ) {
          response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MULTILINE;
          response.placeholder = question.sub_data[0].hint;
          response.label = question.sub_data[0].hint; // Use hint as label
          response.max_length = question.sub_data[0].max_length;
        } else if (
          question.sub_data[0]?.type ===
          EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_SINGLE_LINE
        ) {
          response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_SINGLE_LINE;
          response.placeholder = question.sub_data[0].hint;
          response.label = question.sub_data[0].hint; // Use hint as label
          response.input_type = question.sub_data[0].input_type;
          response.max_length = question.sub_data[0].max_length;
          response.calculation = question.sub_data[0].calculation || null;
          response.unit = question.sub_data[0].unit || null;
        } else if (
          question.sub_data[0]?.type === "input_single_line_with_title"
        ) {
          response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_SINGLE_LINE;
          response.placeholder = question.sub_data[0].hint;
          response.label = question.sub_data[0].hint; // Use hint as label
          response.input_type = question.sub_data[0].input_type;
          response.max_length = question.sub_data[0].max_length;
          response.calculation = question.sub_data[0].calculation || null;
          response.unit = question.sub_data[0].unit || null;
        } else if (question.sub_data[0]?.type === "drop_down") {
          const withInfo = question.sub_data[0]?.with_info || false;
          const dropDownData = question.sub_data[0]?.drop_down;
          response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.SELECT;
          response.options = dropDownData.map((item) => {
            return {
              label: item.component_info,
              value: item.component_info,
              info: item.info,
              capture_hint: item.capture_hint || false,
              capture_options: item.capture_options || false,
              hint: item.hint || "",
              uid: item.uid || null,
            };
          });
          response.with_info = withInfo;
        } else if (question.sub_data[0]?.type === "drop_down_without_title") {
          const withInfo = question.sub_data[0]?.with_info || false;
          const dropDownData = question.sub_data[0]?.drop_down;
          const placeholder = question.sub_data[0]?.hint;
          response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.SELECT;
          response.options = dropDownData?.map((item) => {
            return {
              label: item.component_info,
              value: item.component_info,
              info: item.info,
              capture_hint: item.capture_hint || false,
              capture_options: item.capture_options || false,
              hint: item.hint || "",
              uid: item.uid || null,
            };
          });
          response.with_info = withInfo;
          response.placeholder = placeholder;
        } else if (question.sub_data[0]?.type === "input_medicine") {
          const dropDownData = question.sub_data[0]?.drop_down;
          response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MEDICINE;
          response.unit_options =
            dropDownData?.map((item) => ({
              label: item.component_info,
              value: item.component_info,
            })) || [];
        } else if (question.sub_data[0]?.type === "input_custom_medicine") {
          response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_CUSTOM_MEDICINE;
          response.sub_uid = question.sub_data[0]?.uid;
        } else if (
          question.sub_data[0]?.type === EDIT_TEMPLATE_QUESTION_SCHEMA.LIKERT
        ) {
          response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.LIKERT;
          response.likert_headers = question.sub_data[0]?.likert_headers;
          response.likert_options = question.sub_data[0]?.likert_options;
          response.likert_data = question.sub_data[0]?.likert_data;
        } else if (
          question.sub_data[0]?.type ===
            EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE_OPTION ||
          question.sub_data[0]?.type ===
            EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE
        ) {
          response.schema = question.sub_data[0]?.type;
          response.drop_down_text = question.sub_data[0]?.drop_down_text;
          response.drop_down = question.sub_data[0]?.drop_down;
          // Map dropdown_multiselect from the JSON to sub_data_multiple_selection
          response.sub_data_multiple_selection =
            question.sub_data[0]?.dropdown_multiselect ||
            question.sub_data_multiple_selection ||
            false;
        } else {
          //If the Sub Data is with array with multiple elements with name property
          response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO;
          response.options = question.sub_data.map((item) => {
            if (item?.sub_data) {
              const subQuestionSchema = this.fetchQuestionSchema(item, {
                isSubQuestion: true,
              });
              // Add name and label information for recursive processing
              response._sub_questions.push({
                ...subQuestionSchema,
                _parent_name: item.name, // Store parent name for path construction
                _conditional_parent: question.name,
              });
            }
            return {
              label: item.name,
              value: item.name,
              score: item.value, // Renamed from value to score to avoid conflict with 'value' (label)
              condition: item.condition || null,
              capture_hint: item.capture_hint || false,
              capture_options: item.capture_options || false,
              hint: item.hint || "",
              uid: item.uid || null,
              input_field: item.input_field || null,
              cdss_medical_history_name: item.cdss_medical_history_name || null,
              cdss_family_history: item.cdss_family_history || null,
              cdss_risk_factor_name: item.cdss_risk_factor_name || null,
            };
          });
        }

        response.multiple_selection =
          question.sub_data_multiple_selection ??
          question.multiple_selection ??
          false;
        response.conditional_parrent_values = [question.name] || [];
        return response;
      } else {
        //If there is no sub data, return null
        return {
          schema: null,
          options: [],
          with_info: false,
          multiple_selection: false,
          conditional_parrent_values: [],
        };
      }
    }

    if (question.type === "selection_with_questions") {
      // Determine if this is a "Group of Questions" (e.g. Major Symptoms)
      // or a "Single question with options" (e.g. Whispered Voice Test)
      const isSingleQuestion = question.component_data?.every(
        (item) => !item.question
      );

      if (isSingleQuestion) {
        response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO;
      } else {
        response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_FIELDS;
      }

      // Map component_data options
      response.options =
        question.component_data?.map((item) => {
          return {
            label: item.name,
            value: item.name,
            score_value: item.value,
            condition: item.condition || null,
            capture_hint: item.capture_hint || false,
            capture_options: item.capture_options || false,
            hint: item.hint || "",
            uid: item.uid || null,
            input_field: item.input_field || null,
            is_level_two: item.is_level_two || false,
            cdss_medical_history_name: item.cdss_medical_history_name || null,
            cdss_family_history: item.cdss_family_history || null,
            cdss_risk_factor_name: item.cdss_risk_factor_name || null,
          };
        }) || [];

      response.multiple_selection = question.multiple_selection || false;
      response.info = question.info || null;
      response.required_for_cdss = question.required_for_cdss || false;
      response.cdss_parameter = question.cdss_parameter || null;
      // Essential for useQuestionVisibility hook to recognize this as a valid selection_with_questions type
      // and calculate totals/conditions based on its component_data.
      response.type = question.type;
      response.component_data = question.component_data;

      response.sub_questions =
        question.component_data
          ?.filter((item) => item.sub_data?.length || item.question)
          ?.map((item) => {
            // If this is a single question, sub_questions are triggered by individual options
            const isTriggeredByOption = isSingleQuestion;

            // Determine schema for the sub-question.
            let subSchema = EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO;
            let subOptions = [];

            const targetData = item.sub_data || item.component_data;

            if (targetData && Array.isArray(targetData)) {
              subOptions = targetData.map((opt) => ({
                label: opt.name || opt.label,
                value: opt.name || opt.label,
                score_value: opt.value,
                capture_hint: opt.capture_hint || false,
                capture_options: item.capture_options || false,
                hint: opt.hint || "",
                uid: opt.uid || null,
                input_field: opt.input_field || null,
              }));
            }

            const subNameRaw =
              item.sub_title || item.question || item.name || item.uid;
            const subName = this.convertNameToSchema(subNameRaw);

            return {
              name: subName,
              label: subName,
              schema: subSchema,
              options: subOptions,
              required_for_cdss: item.required_for_cdss || false,
              cdss_parameter: item.cdss_parameter || null,
              uid: item.id || item.uid || null,
              capture_hint: item.capture_hint || false,
              capture_options: item.capture_options || false,
              is_level_two: item.is_level_two || false,
              is_nested: item.is_nested || false,
              skipListView: item.skipListView ?? true,
              multiple_selection: item.multiple_selection || false, // Inner question selection mode
              conditional_parrent_values: isTriggeredByOption
                ? [item.name]
                : [],
              enable_selection_image: item.enable_selection_image || false,
            };
          }) || [];

      // Only return sub-questions that actually have something to render (header/question or options)
      // For single radio questions, we only want the sub_questions triggered by specific options
      if (isSingleQuestion) {
        response.sub_questions = response.sub_questions.filter(
          (sq) => sq.options?.length > 0 || sq.label !== sq.name
        );
      }
      return response;
    }

    //If have component data (For main questions)
    if (question.component_data) {
      //If have component data and type, assume that there is one element in the array
      if (question.component_data && question.component_data?.[0]?.type) {
        const type = question.component_data?.[0]?.type;
        const withInfo = question.component_data?.[0]?.with_info || false;
        const dropDownData = question.component_data?.[0]?.drop_down;
        const placeholder = question.component_data?.[0]?.hint;
        const inputType = question.component_data?.[0]?.input_type;
        const maxLength = question.component_data?.[0]?.max_length;
        const unit = question.component_data?.[0]?.unit || null;

        // Check if there are multiple items in component_data
        const hasMultipleItems = question.component_data.length > 0;

        if (hasMultipleItems && question.multiple_input) {
          // Create MULTIPLE_COMPONENT_DATA schema with sub-questions for each item
          response.schema =
            EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_COMPONENT_DATA;
          response._sub_questions = question.component_data.map(
            (item, index) => {
              const subResponse = {
                schema: null,
                label: item.hint || item.name || `Item ${index + 1}`,
                placeholder: item.hint || "",
                input_type: item.input_type || "text",
                maxLength: item.max_length,
                validation_condition:
                  item.validation || item.validation_condition || null,
                uid: item.uid || null,
                options: [],
                calculation: item.calculation || null,
                capture_hint: item.capture_hint || false,
                capture_options: item.capture_options || false,
                unit: item.unit || null,
              };

              // Determine schema based on item type
              switch (item.type) {
                case "input_single_line":
                case "input_single_line_with_title":
                  subResponse.schema =
                    EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_SINGLE_LINE;
                  break;
                case "input_multiline":
                case "input_multiline_with_title":
                  subResponse.schema =
                    EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MULTILINE;
                  break;
                case "date_picker":
                  subResponse.schema =
                    EDIT_TEMPLATE_QUESTION_SCHEMA.DATE_PICKER;
                  break;
                case "drop_down":
                case "drop_down_without_title":
                  subResponse.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.SELECT;
                  subResponse.options =
                    item.drop_down?.map((opt) => ({
                      label: opt.component_info,
                      value: opt.component_info,
                      info: opt.info,
                      capture_hint: opt.capture_hint || false,
                      capture_options: opt.capture_options || false,
                      hint: opt.hint || "",
                      uid: opt.uid || null,
                    })) || [];
                  subResponse.with_info = item.with_info || false;
                  subResponse.placeholder = item.hint || "Select";
                  break;
                default:
                  subResponse.schema =
                    EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_SINGLE_LINE;
              }

              return subResponse;
            }
          );

          return response;
        }

        switch (type) {
          case "drop_down":
            response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.SELECT;
            response.options = dropDownData.map((item) => {
              return {
                label: item.component_info,
                value: item.component_info,
                info: item.info,
                capture_hint: item.capture_hint || false,
                capture_options: item.capture_options || false,
                hint: item.hint || "",
                uid: item.uid || null,
              };
            });
            response.with_info = withInfo;

            break;
          case "drop_down_without_title":
          case "drop_down_with_title_option":
            response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.SELECT;
            response.options = dropDownData.map((item) => {
              return {
                label: item.component_info,
                value: item.component_info,
                info: item.info,
                capture_hint: item.capture_hint || false,
                capture_options: item.capture_options || false,
                hint: item.hint || "",
                uid: item.uid || null,
              };
            });
            response.with_info = withInfo;
            response.placeholder = placeholder;

            break;
          case "input_single_line":
            response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_SINGLE_LINE;
            response.placeholder = placeholder;
            response.max_length = maxLength;
            response.input_type = inputType;
            response.disabled = question.component_data?.[0]?.disabled || false;
            response.calculated =
              question.component_data?.[0]?.calculated || false;
            response.calculation =
              question.component_data?.[0]?.calculation || null;
            response.validation_condition =
              question.component_data?.[0]?.validation;
            response.unit = unit;

            break;
          case "input_single_line_disabled":
            response.schema =
              EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_SINGLE_LINE_DISABLED;
            response.placeholder = placeholder;
            response.max_length = maxLength;
            response.input_type = inputType;
            response.disabled = true; // Always disabled
            response.calculated =
              question.component_data?.[0]?.calculated || false;
            response.calculation =
              question.component_data?.[0]?.calculation || null;
            response.validation_condition =
              question.component_data?.[0]?.validation;

            break;
          case "input_multiline":
            response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MULTILINE;
            response.placeholder = placeholder;
            response.max_length = maxLength;
            response.input_type = inputType;
            response.validation_condition =
              question.component_data?.[0]?.validation;

            break;
          case "input_multiline_with_title":
            response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MULTILINE;
            response.placeholder =
              question.component_data?.[0]?.info || "Enter here";
            response.max_length = maxLength;
            response.input_type = inputType;
            response.validation_condition =
              question.component_data?.[0]?.validation;

            break;
          case "likert":
            response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.LIKERT;
            response.likert_headers =
              question.component_data?.[0]?.likert_headers;
            response.likert_options =
              question.component_data?.[0]?.likert_options;
            response.likert_data = question.component_data?.[0]?.likert_data;

            // Extract condition from component_data for score-based visibility
            if (question.component_data?.[0]?.condition) {
              response.condition = question.component_data[0].condition;
            }

            break;
          case "selection_with_questions":
            response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_FIELDS;
            // Preserve the required_for_cdss flag for CDSS
            response.required_for_cdss = question.required_for_cdss || false;
            response.cdss_parameter = question.cdss_parameter || null;
            break;
          case "date_picker":
            response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.DATE_PICKER;
            break;
          default:
            response.schema = null;
            break;
        }

        return response;
      } else if (isCustomSchema) {
        response = isCustomSchema?.data;
      } else {
        response.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO;
        response.options = question.component_data.map((item) => {
          return {
            label: item.name,
            value: item.name,
            score_value: item.value, // Added for score calculation
            auto_selected: item.auto_selected || false,
            selection_condition: item.selection_condition || null,
            condition: item.condition || null,
            capture_hint: item.capture_hint || false,
            capture_options: item.capture_options || false,
            hint: item.hint || "",
            uid: item.uid || null,
            input_field: item.input_field || null,
            is_level_two: item.is_level_two || false,
            cdss_medical_history_name: item.cdss_medical_history_name || null,
            cdss_family_history: item.cdss_family_history || null,
            cdss_risk_factor_name: item.cdss_risk_factor_name || null,
          };
        });
        response.multiple_selection = question.multiple_selection || false;
        // Attempt to extract validation from component_data if available (e.g. for implicit single line inputs)
        response.validation_condition =
          question.component_data?.[0]?.validation;
      }
    }

    // Infer isMedicine from component_data sub_data type
    const hasInputMedicine = question.component_data?.some((item) => {
      return item.sub_data?.some(
        (sub) =>
          sub.type === "input_medicine" || sub.type === "input_custom_medicine"
      );
    });

    if (hasInputMedicine) {
      response.isMedicine = true;
    }

    return response;
  }

  static processQuestionRecursively(question, namePath = [], formData = {}) {
    const { schema, options, validation_condition, ...otherProps } =
      this.fetchQuestionSchema(question, {
        isSubQuestion: namePath.length > 1,
      });

    const pathKey =
      question.uid ||
      question.component_name ||
      question.name ||
      question.hint ||
      "";
    const cleanPathKey = pathKey;

    const currentPath = [...namePath, pathKey];

    // Determine the label and subtitle based on the question type
    let questionLabel =
      question.component_name || question.question || question.name;
    let questionSubtitle = null;

    // For input_multiline_with_title, keep component_name as label and hint as subtitle
    if (question.component_data?.[0]?.type === "input_multiline_with_title") {
      questionSubtitle = question.component_data[0].hint;
    }

    // FIX: Ensure selection_with_questions sub_questions have full path names
    if (
      (schema === EDIT_TEMPLATE_QUESTION_SCHEMA.SELECTION_WITH_QUESTIONS ||
        question.type === "selection_with_questions") &&
      otherProps.sub_questions?.length
    ) {
      otherProps.sub_questions = otherProps.sub_questions.map((sq) => ({
        ...sq,
        name: this.getQuestionKey(...currentPath, sq.name),
      }));
    }

    let sub_questions = [];

    let fallbackUid = question.uid || null;

    // Construct the name, appending component UID if applicable for input types
    let finalName = this.getQuestionKey(...currentPath);
    if (
      schema === EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_SINGLE_LINE ||
      schema === EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MULTILINE ||
      schema === EDIT_TEMPLATE_QUESTION_SCHEMA.DATE_PICKER ||
      schema === EDIT_TEMPLATE_QUESTION_SCHEMA.SELECT ||
      schema === EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE ||
      schema === EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE_OPTION
    ) {
      if (question.component_data?.[0]?.uid) {
        finalName = this.getQuestionKey(
          ...currentPath,
          question.component_data[0].uid
        );
      } else if (question.sub_data?.[0]?.uid) {
        finalName = this.getQuestionKey(
          ...currentPath,
          question.sub_data[0].uid
        );
        fallbackUid = question.sub_data[0].uid;
      }
    }

    let questionInput = {
      ...otherProps,
      label:
        schema === EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE_OPTION ||
        schema === EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE
          ? ""
          : questionLabel,
      subtitle: questionSubtitle,
      name: finalName,
      schema,
      options,
      sub_questions,
      initialVisibility:
        question.visibility !== undefined ? question.visibility : true,
      have_option: question.have_option || null,
      have_2_option: question.have_2_option || null,
      is_nested: question.is_nested || false,
      skipListView: question.skipListView ?? true,
      uid: fallbackUid,
      validation_condition: question.validation || validation_condition || null,
      component_data: question.component_data || [],
      enable_selection_image: question.enable_selection_image || false,
    };

    // **Handle have_2_option (Binary/Yes-No wrapper) structure**
    if (question.have_2_option && question.component_data?.length >= 2) {
      questionInput.schema = EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO;
      questionInput.options = question.component_data.map((opt) => ({
        label: opt.name,
        value: opt.name,
        uid: opt.uid || null,
        input_field: opt.input_field || null,
        capture_hint: opt.capture_hint || false,
        capture_options: opt.capture_options || false,
        hint: opt.hint || "",
        cdss_medical_history_name: opt.cdss_medical_history_name || null,
        cdss_family_history: opt.cdss_family_history || null,
        cdss_risk_factor_name: opt.cdss_risk_factor_name || null,
      }));
      questionInput.multiple_selection = question.multiple_selection || false;

      // Iterate through each option in component_data to handle sub-questions
      question.component_data.forEach((option) => {
        if (option?.sub_data?.length) {
          // Level 2: Sub-question triggered by this option (e.g., Body Parts)
          const level2Question = {
            label: `${option.name}`,
            name: this.getQuestionKey(
              ...currentPath,
              option.uid || option.name
            ),
            schema: EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO,
            options: option.sub_data.map((item) => ({
              label: item.name,
              value: item.name,
              uid: item.uid || null,
              input_field: item.input_field || null,
              capture_hint: item.capture_hint || false,
              capture_options: item.capture_options || false,
              hint: item.hint || "",
              cdss_medical_history_name: item.cdss_medical_history_name || null,
              cdss_family_history: item.cdss_family_history || null,
              cdss_risk_factor_name: item.cdss_risk_factor_name || null,
            })),
            sub_questions: [],
            conditional_parrent_values: [option.name],
            multiple_selection:
              option.multiple_selection ||
              question.sub_data_multiple_selection ||
              false,
            uid: option?.uid || null,
            enable_selection_image:
              option.enable_selection_image ||
              question.enable_selection_image ||
              false,
          };

          option.sub_data.forEach((subItem) => {
            if (subItem.sub_data?.length) {
              const nestedPath = [
                ...currentPath,
                option.uid || option.name,
                subItem.uid || subItem.name,
              ];

              const level3Question = {
                label: `${subItem.name}`,
                name: this.getQuestionKey(...nestedPath),
                schema: EDIT_TEMPLATE_QUESTION_SCHEMA.RADIO,
                options: subItem.sub_data.map((item) => ({
                  label: item.name,
                  value: item.name,
                  uid: item.uid || null,
                  input_field: item.input_field || null,
                  capture_hint: item.capture_hint || false,
                  capture_options: item.capture_options || false,
                  hint: item.hint || "",
                  cdss_medical_history_name:
                    item.cdss_medical_history_name || null,
                  cdss_family_history: item.cdss_family_history || null,
                  cdss_risk_factor_name: item.cdss_risk_factor_name || null,
                })),
                sub_questions: [],
                conditional_parrent_values: [subItem.name],
                multiple_selection: subItem.multiple_selection ?? false,
                uid: subItem.uid || null,
                enable_selection_image:
                  subItem.enable_selection_image ||
                  option.enable_selection_image ||
                  question.enable_selection_image ||
                  false,
              };

              level2Question.sub_questions.push(level3Question);
            }
          });

          sub_questions.push(level2Question);
        }
      });

      return questionInput;
    }

    // Recursively process component_data for sub-questions
    if (question.component_data?.length) {
      question.component_data.forEach((item) => {
        // Special Handling for have_option with multiple input-type sub_data items
        if (
          question.have_option &&
          item.name === "Yes" &&
          item?.sub_data?.length > 1 &&
          item.multiple_input &&
          item.sub_data.every(
            (sub) =>
              sub.type === "input_single_line_with_title" ||
              sub.type === "input_single_line" ||
              sub.type === "input_multiline" ||
              sub.type === "drop_down" ||
              sub.type === "drop_down_without_title"
          )
        ) {
          // Iterate all sub_data items to create multiple input questions
          item.sub_data.forEach((subItem, index) => {
            // Wrap subItem in expected structure for fetchQuestionSchema
            const wrappedSubItem = {
              ...subItem,
              name: subItem.hint || subItem.name || `${item.name}_${index}`,
              sub_data: [subItem],
            };

            const { schema, options, ...otherProps } = this.fetchQuestionSchema(
              wrappedSubItem,
              { isSubQuestion: true }
            );

            // Use hint as the sub-path identifier
            const subItemName =
              subItem.uid ||
              subItem.hint ||
              subItem.name ||
              `${item.name}_${index}`;
            const subPath = [
              ...currentPath,
              item.uid || item.name,
              subItemName,
            ];

            // Clean underscores from path parts
            const cleanSubPath = subPath;

            const subQuestion = {
              ...otherProps,
              label: subItem.hint || subItem.name || "", // Use hint as label
              placeholder: subItem.placeholder || subItem.hint || "", // Use placeholder property
              name: this.getQuestionKey(...cleanSubPath),
              uid: subItem.uid || null,
              schema,
              options,
              sub_questions: [],
              initialVisibility: true,
              conditional_parrent_values: [item.name], // Conditional on "Yes"
            };
            sub_questions.push(subQuestion);
          });
        } else if (
          // Special Handling for multiple sub-questions in sub_data with specific types
          item?.sub_data?.length &&
          item.sub_data.some(
            (sub) =>
              sub.type ===
                EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE_OPTION ||
              sub.type === EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE
          )
        ) {
          // Iterate all sub_data items to create multiple questions
          item.sub_data.forEach((subItem, index) => {
            // fetchQuestionSchema expects question.sub_data[0] to contain the schema info
            // So we wrap subItem in the expected structure
            const wrappedSubItem = {
              ...subItem,
              name:
                subItem.item_name || subItem.name || `${item.name}_${index}`,
              sub_data: [subItem],
            };

            const { schema, options, ...otherProps } = this.fetchQuestionSchema(
              wrappedSubItem,
              { isSubQuestion: true }
            );

            // Construct unique name path: Parent -> Option(Yes) -> SubItem(hint)
            // Use hint to match the API data structure where hints are used as keys
            const subItemName =
              subItem.uid ||
              subItem.hint ||
              subItem.item_name ||
              subItem.name ||
              `${item.name}_${index}`;

            const subPath = [
              ...currentPath,
              item.uid || item.name,
              subItemName,
            ];

            // Clean underscores from path parts
            const cleanSubPath = subPath;

            const subQuestion = {
              ...otherProps,
              label: subItem.hint || subItem.name || "", // Use hint as label
              name: this.getQuestionKey(...cleanSubPath),
              schema,
              options,
              sub_questions: [],
              initialVisibility: true,
              conditional_parrent_values: [item.name], // Must come after ...otherProps to override
            };
            sub_questions.push(subQuestion);
          });
        } else if (
          // Special Handling for have_option with a single input field in sub_data
          question.have_option &&
          item.name === "Yes" &&
          item?.sub_data?.length === 1 &&
          (item.sub_data[0].type === "input_single_line_with_title" ||
            item.sub_data[0].type === "input_single_line" ||
            item.sub_data[0].type === "input_multiline" ||
            item.sub_data[0].type === "drop_down" ||
            item.sub_data[0].type === "drop_down_without_title")
        ) {
          const subItem = item.sub_data[0];
          const wrappedSubItem = {
            ...subItem,
            name: subItem.hint || subItem.name || `${item.name}_0`,
            sub_data: [subItem],
          };

          const { schema, options, ...otherProps } = this.fetchQuestionSchema(
            wrappedSubItem,
            { isSubQuestion: true }
          );

          const subItemName =
            subItem.uid || subItem.hint || subItem.name || `${item.name}_0`;
          // Removing item.name ("Yes") from path to match flattened structure in formData
          const subPath = [...currentPath, item.uid || item.name, subItemName];
          const cleanSubPath = subPath;

          const subQuestion = {
            ...otherProps,
            label: subItem.hint || subItem.name || "",
            placeholder: subItem.placeholder || subItem.hint || "",
            name: this.getQuestionKey(...cleanSubPath),
            uid: subItem.uid || null,
            schema,
            options,
            sub_questions: [],
            initialVisibility: true,
            conditional_parrent_values: [item.name],
          };
          sub_questions.push(subQuestion);
        } else {
          // Special handling for selection_with_questions type items
          // These items have a 'question' field that should be used as the name
          if (
            item.type === "selection_with_questions" &&
            (item.question || item.sub_title)
          ) {
            // Create a copy with question as component_name for unique naming
            item = {
              ...item,
              component_name: item.sub_title || item.question,
            };
          }

          // Standard recursive behavior
          const subQuestion = this.processQuestionRecursively(
            item,
            currentPath,
            formData
          );
          // Only add sub-questions with valid schemas
          if (subQuestion.schema) {
            sub_questions.push(subQuestion);
          }
        }
      });
    }

    // Handle _sub_questions from fetchQuestionSchema (for nested radio options)
    if (otherProps._sub_questions?.length) {
      otherProps._sub_questions.forEach((subQuestionSchema, index) => {
        // Special handling for MULTIPLE_COMPONENT_DATA schema
        if (schema === EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_COMPONENT_DATA) {
          // For component_data arrays, use hint as the sub-path identifier
          const subItemLabel =
            subQuestionSchema.uid ||
            subQuestionSchema.label ||
            `Item ${index + 1}`;
          const subPath = [...currentPath, subItemLabel];
          const cleanSubPath = subPath;

          const processedSubQuestion = {
            ...subQuestionSchema,
            name: this.getQuestionKey(...cleanSubPath),
            uid: subQuestionSchema.uid || null,
            sub_questions: [],
          };

          sub_questions.push(processedSubQuestion);
        } else {
          // Original handling for other schema types
          const parentName =
            subQuestionSchema.uid || subQuestionSchema._parent_name;
          const subPath = [...currentPath, parentName];
          const cleanSubPath = subPath;

          const isDropDown =
            subQuestionSchema.schema ===
              EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE_OPTION ||
            subQuestionSchema.schema ===
              EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE;

          const processedSubQuestion = {
            ...subQuestionSchema,
            name: this.getQuestionKey(...cleanSubPath),
            label: isDropDown
              ? ""
              : subQuestionSchema.placeholder || // Use hint for input fields
                `${
                  question.component_name || question.name
                } ${parentName} options`,
            conditional_parrent_values: [parentName],
          };

          delete processedSubQuestion._parent_name;
          delete processedSubQuestion._conditional_parent;

          sub_questions.push(processedSubQuestion);
        }
      });
    }

    return questionInput;
  }
  static buildFieldSchema(subItem) {
    if (subItem) {
      const questions = subItem.generic_data_subData || [];
      console.log(
        "[EditTemplate] Building field schema for subItem:",
        questions
      );
      const subItemUid = subItem.uid || subItem.generic_data_name;
      let input = {
        category_name: this.convertNameToSchema(subItemUid),
        category_label: subItem.generic_data_name,
        uid: subItem.uid || null,
        visibility: subItem.visibility,
        condition: subItem.condition,
        isdynamic: subItem.isdynamic,
        dynamicValue: subItem.dynamicValue,
        questions: [],
      };

      const cleanSubItemUid = subItemUid;

      questions.forEach((question) => {
        // Use the recursive function with initial path containing only the category name
        // START FIX: If question has a UID, use it as the base path (empty prefix) to prevent category prepending
        // SCOPED FIX: Only apply this for drop_down_with_title_option type questions or sub-data
        const isTargetType =
          question.type ===
            EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE_OPTION ||
          question.sub_data?.some(
            (sd) =>
              sd.type ===
              EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE_OPTION
          );

        const initialPath =
          question.uid && isTargetType ? [] : [cleanSubItemUid];

        const questionInput = this.processQuestionRecursively(
          question,
          initialPath
        );
        // END FIX
        input.questions.push(questionInput);
      });

      return input;
    }
  }

  static assignComponentIds(component, currentUid, prefixName = null) {
    const normalizeString = (str) => {
      if (!str) return str;
      return str.toLowerCase().replace(/-/g, "").replace(/\n/g, "");
    };

    if (component.component_data && Array.isArray(component.component_data)) {
      component.component_data.forEach((child, i) => {
        const childUid = `${currentUid}_${i}`;
        child.uid = childUid;
        child.name = prefixName
          ? `${normalizeString(prefixName)}__${normalizeString(childUid)}`
          : childUid;
        this.assignComponentIds(child, childUid, prefixName);
      });
    }

    if (component.sub_questions && Array.isArray(component.sub_questions)) {
      component.sub_questions.forEach((child, i) => {
        const childUid = `${currentUid}_${i}`;
        child.uid = childUid;
        child.name = prefixName
          ? `${normalizeString(prefixName)}__${normalizeString(childUid)}`
          : childUid;
        this.assignComponentIds(child, childUid, prefixName);
      });
    }

    if (component._sub_questions && Array.isArray(component._sub_questions)) {
      component._sub_questions.forEach((child, i) => {
        const childUid = `${currentUid}_${i}`;
        child.uid = childUid;
        child.name = prefixName
          ? `${normalizeString(prefixName)}__${normalizeString(childUid)}`
          : childUid;
        this.assignComponentIds(child, childUid, prefixName);
      });
    }

    if (component.options && Array.isArray(component.options)) {
      component.options.forEach((child, i) => {
        const childUid = `${currentUid}_${i}`;
        child.uid = childUid;
        this.assignComponentIds(child, childUid, prefixName);
      });
    }

    if (component.info && Array.isArray(component.info)) {
      component.info.forEach((child, i) => {
        const childUid = `${currentUid}_${i}`;
        child.uid = childUid;
      });
    }
  }

  static assignDynamicTabIds(
    subCategory,
    basePrefix,
    prefixName = null,
    isFirstTab = false
  ) {
    if (subCategory.raw_json) {
      // Deep clone the raw JSON, replacing the original UID with the new basePrefix
      const originalUid = subCategory.raw_json.uid;

      const rawJsonStr = JSON.stringify(subCategory.raw_json);
      // Replace all occurrences of the exact original UID string
      const newJsonStr = rawJsonStr.split(originalUid).join(basePrefix);
      const newRawJson = JSON.parse(newJsonStr);

      if (prefixName) {
        newRawJson.category_name = prefixName;
        newRawJson.generic_data_name = prefixName;
        if (newRawJson.name) newRawJson.name = prefixName;
      }

      // Rebuild the full field schema from the altered clean raw JSON
      const newSchema = this.buildFieldSchema(newRawJson);

      // Attach top-level properties expected by the rendering logic
      newSchema.uid = basePrefix;
      newSchema.category_name = prefixName || basePrefix;
      newSchema.category_label = prefixName || basePrefix;
      // Pass through the dynamic flags
      newSchema.isdynamic = true;
      newSchema.raw_json = newRawJson;
      if (isFirstTab) {
        newSchema.is_selected = true;
      }

      return newSchema;
    }

    // Fallback for missing raw_json
    const clone = { ...subCategory };
    clone.uid = basePrefix;
    // DO NOT set category_name to basePrefix (e.g. Past Obstetric\nHistory_0) if prefixName is present
    // so it matches the explicit user requirement to use "Child - 1" mapping
    clone.category_name = prefixName || basePrefix;

    if (clone.questions && Array.isArray(clone.questions)) {
      clone.questions = JSON.parse(JSON.stringify(clone.questions));
      clone.questions.forEach((child, i) => {
        const childUid = `${basePrefix}_${i}`;
        child.uid = childUid;

        const normalizeString = (str) =>
          str ? str.toLowerCase().replace(/-/g, "").replace(/\n/g, "") : str;
        child.name = prefixName
          ? `${normalizeString(prefixName)}__${normalizeString(childUid)}`
          : childUid;

        this.assignComponentIds(child, childUid, prefixName);
      });
    }

    if (isFirstTab) {
      clone.is_selected = true;
    }
    return clone;
  }

  static buildConditionalMaps(questionsSchema) {
    const conditionMap = new Map(); // Maps questionName__optionValue -> [UIDs to show]
    const uidToQuestionMap = new Map(); // Maps UID -> { question, fieldName }
    const categoryToQuestionsMap = new Map(); // Maps category name/label -> Set of question objects
    const categoryConditionMap = new Map(); // Maps questionName__optionValue -> [Category names to show]

    const traverseQuestions = (
      questions,
      parentCategories = [],
      parentObjects = []
    ) => {
      if (!questions || !Array.isArray(questions)) return;

      questions.forEach((question) => {
        // Track which questions belong to which categories (including ancestors)
        parentCategories.forEach((catName) => {
          if (catName) {
            if (!categoryToQuestionsMap.has(catName)) {
              categoryToQuestionsMap.set(catName, new Set());
            }
            categoryToQuestionsMap.get(catName).add(question);
          }
        });

        // Add question to UID map if it has a UID
        if (question.uid && question.name) {
          uidToQuestionMap.set(question.uid, {
            question,
            fieldName: question.name,
            parentObjects, // Store the hierarchy for visibility checks
          });
        }

        // Check if this question has options with conditions
        if (question.options && Array.isArray(question.options)) {
          question.options.forEach((option) => {
            if (option.condition) {
              const key = `${question.name}__${option.value}`;

              if (option.condition.position) {
                conditionMap.set(key, option.condition.position);
                if (option.uid) {
                  const uidKey = `${question.name}__${option.uid}`;
                  conditionMap.set(uidKey, option.condition.position);
                }
              }

              if (option.condition.categoryname) {
                const normalizedCatNames = option.condition.categoryname.map(
                  (cat) => EditTemplateHelpers.convertNameToSchema(cat)
                );
                categoryConditionMap.set(key, normalizedCatNames);
                if (option.uid) {
                  const uidKey = `${question.name}__${option.uid}`;
                  categoryConditionMap.set(uidKey, normalizedCatNames);
                }
              }
            }
          });
        }

        // Recursively process sub-questions
        if (question.sub_questions && Array.isArray(question.sub_questions)) {
          traverseQuestions(
            question.sub_questions,
            parentCategories,
            parentObjects
          );
        }

        // Also traverse component_data
        if (question.component_data && Array.isArray(question.component_data)) {
          question.component_data.forEach((item, index) => {
            if (item.uid) {
              const subItemName =
                item.uid || item.hint || item.name || `Item ${index + 1}`;
              const subNameString =
                EditTemplateHelpers.convertNameToSchema(subItemName);
              const generatedName = `${question.name}__${subNameString}`;

              uidToQuestionMap.set(item.uid, {
                question: item,
                fieldName: generatedName,
                parentObjects,
              });

              parentCategories.forEach((catName) => {
                if (catName) {
                  categoryToQuestionsMap.get(catName).add(item);
                }
              });
            }
          });
        }
      });
    };

    // Traverse all categories and their questions
    if (Array.isArray(questionsSchema)) {
      questionsSchema.forEach((category) => {
        const catNameRaw =
          category.category_name ||
          category.name ||
          category.category_label ||
          category.uid;
        const catName = EditTemplateHelpers.convertNameToSchema(catNameRaw);
        const catNames = [catName];
        const catObjects = [category];

        if (category.sub_categories) {
          category.sub_categories.forEach((subCategory) => {
            const subCatNameRaw =
              subCategory.category_name ||
              subCategory.name ||
              subCategory.category_label ||
              subCategory.uid;
            const subCatName =
              EditTemplateHelpers.convertNameToSchema(subCatNameRaw);
            const subCatNames = [...catNames, subCatName];
            const subCatObjects = [...catObjects, subCategory];
            if (subCategory.questions) {
              traverseQuestions(
                subCategory.questions,
                subCatNames,
                subCatObjects
              );
            }
          });
        }

        if (category.generic_data) {
          category.generic_data.forEach((gen) => {
            const genNameRaw = gen.generic_data_name || gen.name || gen.uid;
            const genName = EditTemplateHelpers.convertNameToSchema(genNameRaw);
            const genNames = [...catNames, genName];
            const genObjects = [...catObjects, gen];
            if (gen.generic_data_subData) {
              traverseQuestions(gen.generic_data_subData, genNames, genObjects);
            }
          });
        }

        if (category.questions) {
          traverseQuestions(category.questions, catNames, catObjects);
        }
      });
    }

    return {
      conditionMap,
      uidToQuestionMap,
      categoryConditionMap,
      categoryToQuestionsMap,
    };
  }

  static resetConditionalQuestions(
    questionOrName,
    deselectedValues,
    currentValues,
    formInstance,
    questionsSchema
  ) {
    if (!deselectedValues || deselectedValues.length === 0) return;
    if (!questionsSchema || !formInstance) return;

    const questionName =
      typeof questionOrName === "object" ? questionOrName.name : questionOrName;
    const questionObj =
      typeof questionOrName === "object" ? questionOrName : null;

    // Build the maps
    const {
      conditionMap,
      uidToQuestionMap,
      categoryConditionMap,
      categoryToQuestionsMap,
    } = this.buildConditionalMaps(questionsSchema);

    const currentValuesArray = Array.isArray(currentValues)
      ? currentValues
      : currentValues
        ? [currentValues]
        : [];
    const deselectedValuesArray = Array.isArray(deselectedValues)
      ? deselectedValues
      : deselectedValues
        ? [deselectedValues]
        : [];

    // Helper to get raw value from potential object (to handle inline input values in parent array)
    const getRawValue = (v) =>
      typeof v === "object" && v !== null ? Object.keys(v)[0] : v;

    // Build sets of active UIDs and Categories from current selection
    const activeUIDs = new Set();
    const activeCategories = new Set();

    if (currentValuesArray.length > 0) {
      currentValuesArray.forEach((value) => {
        const valToMatch = getRawValue(value);
        const conditionKey = `${questionName}__${valToMatch}`;

        // Active UIDs
        const uidsToShow = conditionMap.get(conditionKey);
        if (uidsToShow) {
          uidsToShow.forEach((uid) => activeUIDs.add(uid));
        }

        // Active Categories
        const categoriesToShow = categoryConditionMap.get(conditionKey);
        if (categoriesToShow) {
          categoriesToShow.forEach((cat) => activeCategories.add(cat));
        }
      });
    }

    // Function to recursively reset a question and its children
    const recursiveReset = (q) => {
      if (!q) return;

      // Determine the correct field name to reset
      // Priority: 1. q.name from schema (full path-based name), 2. Resolved fieldName from map
      let fieldNameToReset = q.name;
      if (!fieldNameToReset && q.uid) {
        const mappedData = uidToQuestionMap.get(q.uid);
        if (mappedData && mappedData.fieldName) {
          fieldNameToReset = mappedData.fieldName;
        }
      }

      // Reset the field
      if (fieldNameToReset) {
        if (
          q.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_CUSTOM_MEDICINE &&
          formInstance.resetField
        ) {
          // Match the internal clearing logic of InputCustomMedicine
          formInstance.resetField(fieldNameToReset, { defaultValue: [{}] });
        } else {
          // Robustly clear state: Use [] for multiple selection, null for others
          const isMultiple =
            q.multiple_selection ||
            q.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_FIELDS;
          formInstance.setValue(fieldNameToReset, isMultiple ? [] : null);
        }
      }

      // Reset potential internal input fields (from options)
      if (q.options) {
        q.options.forEach((opt) => {
          if (opt.input_field) {
            const inputName =
              opt.input_field.name ||
              `${fieldNameToReset}__${this.normalizeUID(opt.uid)}__${this.normalizeUID(opt.input_field.uid)}`;
            formInstance.setValue(inputName, "");
          }
        });
      }

      // Reset sub-questions
      const allSubQ = [...(q.sub_questions || []), ...(q._sub_questions || [])];
      if (allSubQ.length > 0) {
        allSubQ.forEach((subQ) => recursiveReset(subQ));
      }

      // Reset component_data (if it has fields)
      if (q.component_data && Array.isArray(q.component_data)) {
        q.component_data.forEach((comp) => {
          recursiveReset(comp);
          if (comp.sub_data && Array.isArray(comp.sub_data)) {
            comp.sub_data.forEach((sub) => recursiveReset(sub));
          }
        });
      }

      // Also reset based on UIDs if we can find them in the map
      if (q.uid) {
        const questionData = uidToQuestionMap.get(q.uid);
        if (questionData && questionData.question) {
          const qDef = questionData.question;
          if (qDef.options) {
            qDef.options.forEach((opt) => {
              if (opt.condition && opt.condition.position) {
                opt.condition.position.forEach((childUid) => {
                  const childData = uidToQuestionMap.get(childUid);
                  if (childData && childData.question) {
                    recursiveReset(childData.question);
                  }
                });
              }
              // Also check for category-level children if this option is deselected?
              // That's handled by the main loop in resetConditionalQuestions.
            });
          }
        }
      }
    };

    // 1. Handle UID-based resets (Existing Logic)
    // For each deselected value, find UIDs that should be reset
    const uidsToReset = new Set();
    deselectedValuesArray.forEach((deselectedValue) => {
      const valToMatch = getRawValue(deselectedValue);
      const conditionKey = `${questionName}__${valToMatch}`;
      const uidsControlledByThis = conditionMap.get(conditionKey);

      if (uidsControlledByThis) {
        uidsControlledByThis.forEach((uid) => {
          // Only reset if this UID is not still active from another selection
          if (!activeUIDs.has(uid)) {
            uidsToReset.add(uid);
          }
        });
      }
    });

    // Reset the questions identified by UID
    uidsToReset.forEach((uid) => {
      const questionData = uidToQuestionMap.get(uid);
      if (questionData && questionData.question) {
        recursiveReset(questionData.question);
      }
    });

    // 1.7 Handle Internal Sub-Questions (tied to specific options)
    // These are questions defined in the question.sub_questions or question._sub_questions array
    const allSubQuestions = [
      ...(questionObj?.sub_questions || []),
      ...(questionObj?._sub_questions || []),
    ];

    if (allSubQuestions.length > 0) {
      // Build sets of deselected and current keys (labels and UIDs) for robust matching
      const deselectedKeys = new Set(deselectedValuesArray.map(getRawValue));
      const currentKeys = new Set(currentValuesArray.map(getRawValue));

      if (questionObj?.component_data) {
        questionObj.component_data.forEach((item) => {
          const isDeselected =
            deselectedValues.includes(item.name) ||
            (item.uid && deselectedValues.includes(item.uid));
          const isStillSelected =
            currentValues.includes(item.name) ||
            (item.uid && currentValues.includes(item.uid));

          if (isDeselected) {
            if (item.name) deselectedKeys.add(item.name);
            if (item.uid) deselectedKeys.add(item.uid);
          }
          if (isStillSelected) {
            if (item.name) currentKeys.add(item.name);
            if (item.uid) currentKeys.add(item.uid);
          }
        });
      }

      allSubQuestions.forEach((subQ) => {
        // Check if this sub-question is triggered by any of the deselected values (both label and UID)
        const isTriggeredByDeselected = Array.from(deselectedKeys).some((dk) =>
          subQ.conditional_parrent_values?.includes(dk)
        );

        // Check if it's still triggered by any of the remaining current values
        const isTriggeredByCurrent = Array.from(currentKeys).some((ck) =>
          subQ.conditional_parrent_values?.includes(ck)
        );

        if (isTriggeredByDeselected && !isTriggeredByCurrent) {
          recursiveReset(subQ);
        }
      });
    }

    // 1.8 Handle Inline Input Fields (input_field in radio options)
    if (questionObj && questionObj.options) {
      questionObj.options.forEach((option) => {
        if (option.input_field) {
          const isDeselected =
            deselectedValues.includes(option.value) ||
            (option.uid && deselectedValues.includes(option.uid));
          const isStillSelected =
            currentValues.includes(option.value) ||
            (option.uid && currentValues.includes(option.uid));

          if (isDeselected && !isStillSelected) {
            // Generate the field name using the same logic as RadioWithAutoSelection
            const fieldNameToReset = option.input_field.uid
              ? `${questionName}__${this.normalizeUID(option.uid)}__${this.normalizeUID(option.input_field.uid)}`
              : option.input_field.name;

            if (fieldNameToReset) {
              formInstance.setValue(fieldNameToReset, null);
            }
          }
        }
      });
    }

    // 1.5 Handle Category-based resets
    const categoriesToReset = new Set();
    deselectedValues.forEach((deselectedValue) => {
      const conditionKey = `${questionName}__${deselectedValue}`;
      const categoriesControlledByThis = categoryConditionMap.get(conditionKey);

      if (categoriesControlledByThis) {
        categoriesControlledByThis.forEach((catName) => {
          if (!activeCategories.has(catName)) {
            categoriesToReset.add(catName);
          }
        });
      }
    });

    categoriesToReset.forEach((catName) => {
      const normalizedCatName =
        EditTemplateHelpers.convertNameToSchema(catName);
      const questionsInCat = categoryToQuestionsMap.get(normalizedCatName);

      if (questionsInCat) {
        questionsInCat.forEach((q) => {
          recursiveReset(q);
        });
      }
    });
  }

  // Delegate to EditTemplateDataTransformer
  static transform_formData_to_ApiFormat(formData, templateName) {
    console.log(" Default Values for form", formData);

    return EditTemplateDataTransformer.transform_formData_to_ApiFormat(
      formData,
      templateName
    );
  }

  // Transform api raw data to form data
  static transform_api_raw_data_to_form_data(raw_data, templateName) {
    return EditTemplateDataTransformer.transform_api_raw_data_to_form_data(
      raw_data,
      templateName
    );
  }

  static getQuestionKey(...nameParts) {
    // Filter out null, undefined, and empty string values
    const validParts = nameParts.filter(
      (part) => part !== null && part !== undefined && part !== ""
    );

    // Convert each part to schema format and join with divider
    return validParts
      .map((part) => this.convertNameToSchema(part))
      .join(this.divider);
  }

  static validateValue(question, value) {
    if (
      question?.input_type === "number" &&
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      const num = Number(value);
      if (!isNaN(num) && num < 0) {
        return "0";
      }
    }
    return value;
  }

  static getRelatedQuestionValues(
    question,
    formData,
    parentValue,
    useUid = false
  ) {
    const response = {};
    const parentValuesRaw = Array.isArray(parentValue)
      ? parentValue
      : [parentValue];

    const parentValues = parentValuesRaw.map((v) => {
      // If v is an object like { "UID": "value" }, extract the key.
      if (typeof v === "object" && v !== null && !Array.isArray(v)) {
        return Object.keys(v)[0];
      }
      return v;
    });

    const targetQuestions = question?.sub_questions?.filter((sub_question) => {
      // If no conditional values specified, it's an unconditional sub-question (always show/process)
      if (!sub_question?.conditional_parrent_values?.length) return true;

      return sub_question?.conditional_parrent_values?.some((val) => {
        const nVal = this.normalizeUID(val);
        return parentValues?.some(
          (pv) => this.normalizeUID(pv) === nVal || pv === val
        );
      });
    });

    const processedParentItems = new Set();

    if (targetQuestions?.length) {
      parentValues?.forEach((item) => {
        const nItem = this.normalizeUID(item);
        const subQuestionsForThisValue = targetQuestions?.filter((sub_q) => {
          // If sub_q has no conditional parents, it belongs to ALL values (unconditional)
          if (!sub_q.conditional_parrent_values?.length) return true;
          return sub_q.conditional_parrent_values?.some(
            (v) => this.normalizeUID(v) === nItem || v === item
          );
        });

        if (subQuestionsForThisValue?.length) {
          processedParentItems.add(nItem);
          const shouldUseArrayFormat =
            subQuestionsForThisValue.length > 1 ||
            subQuestionsForThisValue.some(
              (sq) =>
                sq.schema ===
                  EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE_OPTION ||
                sq.schema ===
                  EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE ||
                sq.schema ===
                  EDIT_TEMPLATE_QUESTION_SCHEMA.SELECTION_WITH_QUESTIONS ||
                sq.schema === "drop_down" ||
                sq.schema === "drop_down_without_title"
            );

          const subValues = {};
          const subValuesArray = []; // Array to hold individual sub-question objects

          subQuestionsForThisValue.forEach((sub_q) => {
            let val = formData[sub_q.name];
            val = this.validateValue(sub_q, val);

            if (
              val !== undefined &&
              val !== null &&
              val !== "" &&
              !(Array.isArray(val) && val.length === 0)
            ) {
              // Handle nested sub-questions recursively
              const deepRelated = this.getRelatedQuestionValues(
                sub_q,
                formData,
                val,
                useUid
              );
              let finalValue;
              if (Object.keys(deepRelated).length > 0) {
                // If deepRelated is an array-like structure from a selection type
                if (Array.isArray(val)) {
                  // Merge all related data into a single object
                  finalValue = val.reduce((acc, v) => {
                    const vKey =
                      typeof v === "object" && v !== null && !Array.isArray(v)
                        ? Object.keys(v)[0]
                        : v;

                    const related = deepRelated[vKey] || {};
                    if (Object.keys(related).length > 0) {
                      const option = sub_q.options?.find(
                        (o) =>
                          (o.value || o.uid || o.label) === vKey ||
                          o.name === vKey
                      );
                      const key = useUid && option?.uid ? option.uid : vKey;
                      acc[key] = related;
                    } else if (
                      typeof v === "object" &&
                      v !== null &&
                      !Array.isArray(v)
                    ) {
                      // If it's an object but has no deepRelated (e.g. just an input field),
                      // just keep the object as-is in the reduced result
                      acc[vKey] = v[vKey];
                      // wait, if we want to return the object itself { UID: val }
                      // actually, if deepRelated is empty, this whole block is skipped.
                    }
                    return acc;
                  }, {});
                } else {
                  // For single selection, extract the value from deepRelated
                  const valKey =
                    typeof val === "object" &&
                    val !== null &&
                    !Array.isArray(val)
                      ? Object.keys(val)[0]
                      : val;
                  finalValue = deepRelated[valKey] || deepRelated;
                }
              } else {
                if (Array.isArray(val)) {
                  // Map each selected value; if an option has input_field, look up its form value
                  finalValue = val.map((v) => {
                    const option = sub_q.options?.find(
                      (o) => o.value === v || o.label === v
                    );
                    if (option?.input_field && option?.uid && sub_q.name) {
                      const inputFieldKey = `${sub_q.name}__${this.normalizeUID(option.uid)}__${this.normalizeUID(option.input_field?.uid)}`;
                      const inputFieldValue = formData[inputFieldKey];
                      if (
                        inputFieldValue !== undefined &&
                        inputFieldValue !== null &&
                        inputFieldValue !== ""
                      ) {
                        return { [option.uid]: inputFieldValue };
                      }
                    }
                    return v; // plain string — no input_field or no value typed
                  });
                } else {
                  finalValue = val;
                }
              }

              const forceUid =
                shouldUseArrayFormat ||
                sub_q.schema ===
                  EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE_OPTION ||
                sub_q.schema ===
                  EDIT_TEMPLATE_QUESTION_SCHEMA.DROP_DOWN_WITH_TITLE;

              const key =
                useUid || forceUid
                  ? sub_q.uid || sub_q.label?.trim() || sub_q.name?.trim()
                  : sub_q.label?.trim() || sub_q.name?.trim();
              if (key) {
                let valueToStore;
                if (
                  typeof finalValue === "object" &&
                  Object.keys(finalValue).length > 0
                ) {
                  valueToStore = finalValue;
                } else {
                  // If no deep structure, use the value itself
                  valueToStore = Array.isArray(val) ? val : val;
                }

                // If sub-question is an input type and has a UID, wrap value in UID object
                // This pertains to input_single_line, input_single_line_with_title, input_multiline, input_multiline_with_title, date_picker
                // which are mapped to INPUT_SINGLE_LINE, INPUT_MULTILINE and DATE_PICKER schemas.
                if (
                  !shouldUseArrayFormat &&
                  (sub_q.schema ===
                    EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_SINGLE_LINE ||
                    sub_q.schema ===
                      EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MULTILINE ||
                    sub_q.schema ===
                      EDIT_TEMPLATE_QUESTION_SCHEMA.DATE_PICKER) &&
                  sub_q.uid
                ) {
                  valueToStore = { [sub_q.uid]: valueToStore };
                }

                // Store in both formats for backward compatibility
                subValues[key] = valueToStore;
                // Store as individual array element
                subValuesArray.push({ [key]: valueToStore });
              }
            }
          });

          if (Object.keys(subValues).length > 0) {
            // Check if this is a drop_down_with_title_option or selection_with_questions pattern
            // These should use array format
            if (shouldUseArrayFormat && subValuesArray.length > 0) {
              response[item] = subValuesArray;
            } else if (Object.keys(subValues).length === 1) {
              // Flatten if there is only one sub-question
              const firstKey = Object.keys(subValues)[0];
              response[item] = subValues[firstKey];
            } else {
              response[item] = subValues;
            }
          }
        }
      });

      // If parentValue is empty string or undefined (for MULTIPLE_FIELDS containers)
      // This handles selection_with_questions at the top level
      if (
        (!parentValue || parentValue === "") &&
        targetQuestions.some((q) => !q.conditional_parrent_values?.length)
      ) {
        const unconditionalQuestions = targetQuestions.filter(
          (q) => !q.conditional_parrent_values?.length
        );
        const resultArray = [];

        unconditionalQuestions.forEach((sub_q) => {
          let val = formData[sub_q.name];
          val = this.validateValue(sub_q, val);

          // NEW: Filter out invalid "Yes" values for selection_with_questions
          if (
            val === "Yes" &&
            sub_q.component_data &&
            Array.isArray(sub_q.component_data)
          ) {
            const hasYesOption = sub_q.component_data.some(
              (opt) =>
                opt.name === "Yes" || opt.uid === "Yes" || opt.label === "Yes"
            );
            if (!hasYesOption) {
              val = ""; // Invalidate "Yes" if not a valid option
            }
          }

          if (
            val !== undefined &&
            val !== null &&
            val !== "" &&
            !(Array.isArray(val) && val.length === 0)
          ) {
            const deepRelated = this.getRelatedQuestionValues(
              sub_q,
              formData,
              val,
              useUid
            );
            const key = useUid
              ? sub_q.uid || sub_q.label || sub_q.name
              : sub_q.label || sub_q.name;

            if (Object.keys(deepRelated).length > 0) {
              if (Array.isArray(val)) {
                resultArray.push({
                  [key]: val
                    .map((v) => {
                      if (
                        typeof v === "object" &&
                        v !== null &&
                        !Array.isArray(v)
                      ) {
                        // If v is an object, use its key for deepRelated lookup
                        const vKey = Object.keys(v)[0];
                        const mappedObject = deepRelated[vKey] || {};

                        // If no sub-data found for this object, return the object itself
                        // This avoids wrapping it in its own key or converting to [object Object]
                        if (Object.keys(mappedObject).length === 0) {
                          return v;
                        }

                        return { [vKey]: mappedObject };
                      }
                      const mappedObject = deepRelated[v] || {};
                      // Check if this specific option has an input field
                      const option = sub_q.options?.find(
                        (o) => o.value === v || o.label === v
                      );
                      if (option?.input_field && option?.uid && sub_q.name) {
                        const inputFieldKey = `${sub_q.name}__${this.normalizeUID(option.uid)}__${this.normalizeUID(option.input_field?.uid)}`;
                        const inputFieldValue = formData[inputFieldKey];
                        if (
                          inputFieldValue !== undefined &&
                          inputFieldValue !== null &&
                          inputFieldValue !== ""
                        ) {
                          return { [option.uid]: inputFieldValue };
                        }
                      }

                      // If we have mappedObject but no specific input field handling above, wrap it
                      if (Object.keys(mappedObject).length > 0) {
                        return { [v]: mappedObject };
                      }
                      return v;
                    })
                    .filter((v, index, self) => {
                      if (
                        typeof v === "object" &&
                        v !== null &&
                        !Array.isArray(v)
                      ) {
                        const k = this.normalizeUID(Object.keys(v)[0]);
                        const firstIndex = self.findIndex(
                          (item) =>
                            typeof item === "object" &&
                            item !== null &&
                            !Array.isArray(item) &&
                            this.normalizeUID(Object.keys(item)[0]) === k
                        );
                        return firstIndex === index;
                      }
                      return true;
                    }),
                });
              } else {
                resultArray.push({
                  [key]: [{ [val]: deepRelated }],
                });
              }
            } else {
              if (Array.isArray(val)) {
                resultArray.push({
                  [key]: val
                    .map((v) => {
                      if (
                        typeof v === "object" &&
                        v !== null &&
                        !Array.isArray(v)
                      ) {
                        return v;
                      }
                      const option = sub_q.options?.find(
                        (o) => o.value === v || o.label === v
                      );
                      if (option?.input_field && option?.uid && sub_q.name) {
                        const inputFieldKey = `${sub_q.name}__${this.normalizeUID(option.uid)}__${this.normalizeUID(option.input_field?.uid)}`;
                        const inputFieldValue = formData[inputFieldKey];
                        if (
                          inputFieldValue !== undefined &&
                          inputFieldValue !== null &&
                          inputFieldValue !== ""
                        ) {
                          return { [option.uid]: inputFieldValue };
                        }
                      }
                      return v;
                    })
                    .filter((v, index, self) => {
                      if (
                        typeof v === "object" &&
                        v !== null &&
                        !Array.isArray(v)
                      ) {
                        const k = this.normalizeUID(Object.keys(v)[0]);
                        const firstIndex = self.findIndex(
                          (item) =>
                            typeof item === "object" &&
                            item !== null &&
                            !Array.isArray(item) &&
                            this.normalizeUID(Object.keys(item)[0]) === k
                        );
                        return firstIndex === index;
                      }
                      return true;
                    }),
                });
              } else {
                resultArray.push({
                  [key]: Array.isArray(val) ? val : [val],
                });
              }
            }
          }
        });

        // Check if this is a selection_with_questions OR container pattern
        const isSelectionWithQuestions =
          question?.schema ===
            EDIT_TEMPLATE_QUESTION_SCHEMA.SELECTION_WITH_QUESTIONS ||
          question?.schema ===
            EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_COMPONENT_DATA ||
          question?.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_FIELDS;

        if (isSelectionWithQuestions && resultArray.length > 0) {
          // Return as array for selection_with_questions
          return resultArray;
        } else if (resultArray.length > 0) {
          // Merge into response object for other types
          resultArray.forEach((item) => {
            const itemKey = Object.keys(item)[0];
            response[itemKey] = item[itemKey];
          });
        }
      }

      // TARGETED FIX: Only for parent questions (those with sub_questions) that are NOT SELECTION_WITH_QUESTIONS
      // This preserves "Others" input data for questions like "Does patient have any comorbidities?"
      const isContainer =
        question?.schema ===
          EDIT_TEMPLATE_QUESTION_SCHEMA.SELECTION_WITH_QUESTIONS ||
        question?.schema ===
          EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_COMPONENT_DATA ||
        question?.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.MULTIPLE_FIELDS;

      if (!isContainer && question?.sub_questions?.length > 0) {
        parentValuesRaw.forEach((v) => {
          if (typeof v === "object" && v !== null && !Array.isArray(v)) {
            const vKey = Object.keys(v)[0];
            const normalizedVKey = this.normalizeUID(vKey);
            if (!processedParentItems.has(normalizedVKey)) {
              // Find the option label for these "Others" types
              const option = question.options?.find(
                (o) =>
                  this.normalizeUID(o.uid) === normalizedVKey ||
                  this.normalizeUID(o.name) === normalizedVKey
              );
              const displayKey = option?.label || vKey;
              response[displayKey] = v[vKey];
            }
          }
        });
      }
    }

    return response;
  }

  static getSelectionWithQuestionsRelatedValues(question, formData) {
    const list = [];

    if (!Array.isArray(question?.sub_questions)) return list;

    const mapUidToValue = (q, raw) => {
      const rawStr = String(raw);
      const opt = (q.options || []).find(
        (o) => o.uid === rawStr || o.value === rawStr || o.label === rawStr
      );
      return opt ? opt.value || opt.label : rawStr;
    };

    question.sub_questions.forEach((sub_q) => {
      let val = this.validateValue(sub_q, formData[sub_q.name]);
      if (!val || (Array.isArray(val) && val.length === 0)) return;

      const mainKey = sub_q.label?.trim() || sub_q.name?.trim();
      const selected = (Array.isArray(val) ? val : [val]).map((v) =>
        mapUidToValue(sub_q, v)
      );

      // allow multi-select options to be joined instead of picking only the first
      const mainValue = selected.join(", ");
      list.push({
        item_name: mainKey,
        value: mainValue,
        required_for_cdss: !!sub_q.required_for_cdss,
        cdss_parameter: sub_q.cdss_parameter || null,
      });

      // if Inconclusive, push nested row right after
      if (mainValue === "Inconclusive" && Array.isArray(sub_q.sub_questions)) {
        sub_q.sub_questions.forEach((nestedQ) => {
          let nestedVal = this.validateValue(nestedQ, formData[nestedQ.name]);
          if (
            !nestedVal ||
            (Array.isArray(nestedVal) && nestedVal.length === 0)
          )
            return;

          const nestedKey = nestedQ.label?.trim() || "Otoscopic Examination";
          const nestedValue = Array.isArray(nestedVal)
            ? nestedVal.map((v) => mapUidToValue(nestedQ, v)).join(", ")
            : mapUidToValue(nestedQ, nestedVal);

          list.push({
            item_name: nestedKey,
            value: nestedValue,
            required_for_cdss: !!nestedQ.required_for_cdss,
            cdss_parameter: nestedQ.cdss_parameter || null,
          });
        });
      }
    });

    return list;
  }

  static formatValue(value) {
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    return value;
  }

  static formatVisitDate(date) {
    const d = date ? new Date(date) : new Date();
    if (isNaN(d.getTime())) return "";

    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const seconds = d.getSeconds().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const strTime =
      hours.toString().padStart(2, "0") +
      ":" +
      minutes +
      ":" +
      seconds +
      " " +
      ampm;

    return `${day}/${month}/${year} ${strTime}`;
  }

  static getFieldPathDetails(fieldName, categories) {
    if (!categories || !Array.isArray(categories)) return null;
    let result = null;

    const traverse = (
      questions,
      parentPath,
      catName,
      subCatName,
      catLabel,
      subCatLabel
    ) => {
      if (!questions || !Array.isArray(questions)) return;
      for (const q of questions) {
        if (result) return;

        const currentPath = [...parentPath, q.label || q.name];

        const buildFullPath = (pathArr) => {
          const fullPath = [];
          if (catLabel || catName) fullPath.push(catLabel || catName);
          if (subCatLabel || subCatName)
            fullPath.push(subCatLabel || subCatName);
          return [...fullPath, ...pathArr].join(" > ");
        };

        // Match exact field name
        if (q.name === fieldName || q.uid === fieldName) {
          result = {
            questionPath: buildFullPath(currentPath),
            categoryName: catName,
            subCategoryName: subCatName,
            uid: q.uid || q.name,
          };
          return;
        }

        // Check sub_questions
        if (q.sub_questions && Array.isArray(q.sub_questions)) {
          traverse(
            q.sub_questions,
            currentPath,
            catName,
            subCatName,
            catLabel,
            subCatLabel
          );
          if (result) return;
        }

        // Check component_data (e.g. Systolic/Diastolic inside a generic parent)
        if (q.component_data && Array.isArray(q.component_data)) {
          for (let i = 0; i < q.component_data.length; i++) {
            const item = q.component_data[i];
            const itemLabel = item.hint || item.name || `Item ${i + 1}`;
            const itemGeneratedName = `${q.name}__${itemLabel.replace(/\s+/g, "_").toLowerCase()}`;

            if (
              item.uid === fieldName ||
              itemGeneratedName === fieldName ||
              item.name === fieldName
            ) {
              result = {
                questionPath: buildFullPath([...currentPath, itemLabel]),
                categoryName: catName,
                subCategoryName: subCatName,
                uid: item.uid || fieldName,
              };
              return;
            }
          }
        }
      }
    };

    categories.forEach((cat) => {
      if (result) return;
      if (cat.sub_categories) {
        cat.sub_categories.forEach((subCat) => {
          if (result) return;
          traverse(
            subCat.questions,
            [],
            cat.category_name,
            subCat.category_name,
            cat.category_label || cat.category_name,
            subCat.category_label || subCat.category_name
          );
        });
      } else if (cat.questions) {
        traverse(
          cat.questions,
          [],
          cat.category_name,
          null,
          cat.category_label || cat.category_name,
          null
        );
      }
    });

    return result;
  }

  static validateGenericSubQuestions(formData, categories) {
    const errors = [];

    const validateRecursively = (
      questionsList,
      parentPath = [],
      categoryInfo = {}
    ) => {
      if (!questionsList || !Array.isArray(questionsList)) return;

      questionsList.forEach((question) => {
        // 1. Validate this question
        const value = formData[question.name];

        const currentPath = [...parentPath, question.label || question.name];

        // Find active sub-questions based on value
        if (question.sub_questions && question.sub_questions.length > 0) {
          // Logic from SubQuestionController to determine visibility
          let activeSubQuestions = [];
          const isValueArray = Array.isArray(value);

          if (isValueArray) {
            activeSubQuestions = question.sub_questions.filter((subQ) =>
              subQ.conditional_parrent_values?.some((parentVal) =>
                value.includes(parentVal)
              )
            );
          } else if (value !== undefined && value !== null && value !== "") {
            // Ensure value is present and handle object values
            const valStr = typeof value === "string" ? value : String(value);
            activeSubQuestions = question.sub_questions.filter((subQ) =>
              subQ.conditional_parrent_values?.some(
                (cpv) => String(cpv).toLowerCase() === valStr.toLowerCase()
              )
            );
          }

          if (activeSubQuestions.length > 0) {
            // Filter out medicine sub-questions as they have dedicated validation
            const nonMedicineSubQuestions = activeSubQuestions.filter(
              (subQ) =>
                subQ.schema !== EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MEDICINE &&
                subQ.schema !==
                  EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_CUSTOM_MEDICINE
            );

            // Check if ANY of the active sub-questions have a value in formData
            const missingSubQuestions = nonMedicineSubQuestions.filter(
              (subQ) => {
                // Skip calculated or disabled fields as they are system-managed or read-only
                // and may not have been populated yet if their tab hasn't been opened
                if (subQ.calculation || subQ.calculated || subQ.disabled)
                  return false;

                const subValue = formData[subQ.name];
                // Check if value is "empty"
                if (
                  subValue === undefined ||
                  subValue === null ||
                  subValue === ""
                )
                  return true;
                if (Array.isArray(subValue) && subValue.length === 0)
                  return true;
                return false;
              }
            );

            if (missingSubQuestions.length > 0) {
              const missingNames = missingSubQuestions
                .map((q) => q.label)
                .join(", ");
              errors.push({
                field: question.name,
                message: `Please complete the following details for : '${missingNames}'.`,
                questionPath: currentPath.join(" > "),
                uid: question.uid || question.name,
                categoryName: categoryInfo.categoryName,
                subCategoryName: categoryInfo.subCategoryName,
              });
            }
          }
        }

        // 2. Validate Inline Inputs (input_field) within radio/checkbox options
        // Skip for medicine schemas as they manage arrays of objects natively and have dedicated validation below
        if (
          question.schema !== EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MEDICINE &&
          question.schema !==
            EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_CUSTOM_MEDICINE &&
          question.schema !== EDIT_TEMPLATE_QUESTION_SCHEMA.LIKERT
        ) {
          const values = Array.isArray(value) ? value : value ? [value] : [];
          const missingInlineInputs = [];

          // Collect all possible options to check for input_field definitions
          const allOptions = [
            ...(question.options || []),
            ...(question.component_data || []),
            ...(question.sub_data || []),
          ];

          // Part A: Check existing object values for empty content
          values.forEach((val) => {
            if (typeof val === "object" && val !== null) {
              const uid = Object.keys(val)[0];
              const inputVal = val[uid];

              if (
                inputVal === undefined ||
                inputVal === null ||
                String(inputVal).trim() === ""
              ) {
                // Try to find the label for this UID from the question options
                let optionLabel = "details";
                const matchedOpt = allOptions.find((opt) => opt.uid === uid);
                if (matchedOpt) {
                  optionLabel =
                    matchedOpt.label || matchedOpt.name || "details";
                }
                missingInlineInputs.push(optionLabel);
              }
            }
          });

          // Part B: Check for selected strings that REQUIRE an input_field but don't have an object yet
          values.forEach((val) => {
            if (typeof val === "string") {
              const option = allOptions.find(
                (opt) => opt.value === val || opt.uid === val
              );
              if (option?.input_field) {
                const targetUid = option.uid;
                const objectExists = values.some(
                  (v) =>
                    typeof v === "object" &&
                    v !== null &&
                    EditTemplateHelpers.normalizeUID(Object.keys(v)[0]) ===
                      EditTemplateHelpers.normalizeUID(targetUid)
                );

                if (!objectExists) {
                  missingInlineInputs.push(
                    option.label || option.name || "details"
                  );
                }
              }
            }
          });

          if (missingInlineInputs.length > 0) {
            console.log("k.k.k", question);
            // Deduplicate labels
            const uniqueMissing = [...new Set(missingInlineInputs)];
            errors.push({
              field: question.name,
              message: `Please enter details for: '${uniqueMissing.join(", ")}'.`,
              questionPath: currentPath.join(" > "),
              uid: question.uid || question.name,
              categoryName: categoryInfo.categoryName,
              subCategoryName: categoryInfo.subCategoryName,
            });
          }
        }
        // Special Validation for isMedicine (e.g., Current Medication, Past Medication)
        if (question.isMedicine) {
          const checkYes = (v) => String(v).toLowerCase() === "yes";
          const hasYes =
            checkYes(value) || (Array.isArray(value) && value.some(checkYes));

          if (hasYes) {
            // Find the medicine input sub-question field name
            // Medicine data is stored in a sub-question, not in the parent question field
            let medicineFieldName = null;

            // Strategy 1: Look for medicine schemas in sub_questions (recursively)
            const findMedicineSubQuestion = (q) => {
              if (
                q.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_MEDICINE ||
                q.schema === EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_CUSTOM_MEDICINE
              ) {
                return q;
              }
              // Check both standard and internal sub-questions
              const subQs = [
                ...(q.sub_questions || []),
                ...(q._sub_questions || []),
              ];
              if (subQs.length > 0) {
                for (const sq of subQs) {
                  const found = findMedicineSubQuestion(sq);
                  if (found) return found;
                }
              }
              return null;
            };

            const medicineSubQ = findMedicineSubQuestion(question);
            if (medicineSubQ) {
              medicineFieldName = medicineSubQ.name;
            }

            // Strategy 2: Try common patterns if not found or if the found sub-question name isn't in formData
            if (
              !medicineFieldName ||
              formData[medicineFieldName] === undefined
            ) {
              const divider = EditTemplateHelpers.divider || "__";
              const questionNameSchema =
                EditTemplateHelpers.convertNameToSchema(question.name);

              const possibleNames = [
                `${question.name}${divider}yes`,
                `${question.name}${divider}Yes`,
                `${questionNameSchema}${divider}yes`,
                `${questionNameSchema}${divider}Yes`,
                question.name,
                questionNameSchema,
              ];

              for (const name of possibleNames) {
                if (formData[name]) {
                  medicineFieldName = name;
                  break;
                }
              }
            }

            // Get medicine data from the correct field
            const medicineData = medicineFieldName
              ? formData[medicineFieldName]
              : null;

            // 1. Check if any medicine is added
            // Filter out "Yes" or any other non-object values that might be in the array
            const distinctMedicines = Array.isArray(medicineData)
              ? medicineData.filter(
                  (item) => typeof item === "object" && item !== null
                )
              : [];

            console.log("[Medicine Validation]", {
              questionName: question.name,
              medicineFieldName,
              medicineData,
              distinctMedicines,
              distinctCount: distinctMedicines.length,
            });

            if (distinctMedicines.length === 0) {
              errors.push({
                field: question.name, // Use parent question name for error
                message: `Please add at least one medicine for ${
                  question.label || question.name
                }`,
                questionPath: currentPath.join(" > "),
                uid: question.uid || question.name,
                categoryName: categoryInfo.categoryName,
                subCategoryName: categoryInfo.subCategoryName,
              });
            } else {
              // 2. Check if all fields are filled in the added rows
              // Reuse recursive finder
              const medicineSubQ = findMedicineSubQuestion(question);

              const isCustom =
                medicineSubQ?.schema ===
                EDIT_TEMPLATE_QUESTION_SCHEMA.INPUT_CUSTOM_MEDICINE;

              const hasIncompleteRows = distinctMedicines.some((item) => {
                const keys = Object.keys(item);
                if (keys.length === 0) return true; // empty row

                if (isCustom) {
                  // For custom medicine, only the primary field (Medicine name) is required.
                  // This is always the first key in the object.
                  const primaryKey = keys[0];
                  return (
                    !primaryKey ||
                    item[primaryKey] === undefined ||
                    item[primaryKey] === null ||
                    item[primaryKey] === ""
                  );
                }
                return (
                  !item.medicine_name ||
                  !item.dosage ||
                  !item.unit ||
                  item.medicine_name === "" ||
                  item.dosage === "" ||
                  item.unit === ""
                );
              });

              console.log("[Medicine Validation - Completeness]", {
                hasIncompleteRows,
                isCustom,
                medicines: distinctMedicines,
              });

              if (hasIncompleteRows) {
                // Collect missing field hints for a better message
                const missingHints = new Set();
                distinctMedicines.forEach((item) => {
                  const keys = Object.keys(item);
                  const keysToCheck =
                    isCustom && keys.length > 0 ? [keys[0]] : keys;

                  keysToCheck.forEach((k) => {
                    if (
                      item[k] === undefined ||
                      item[k] === null ||
                      item[k] === ""
                    ) {
                      missingHints.add(k);
                    }
                  });
                });
                const missingDetailsStr =
                  missingHints.size > 0
                    ? ` (${Array.from(missingHints).join(", ")})`
                    : "";

                errors.push({
                  field: question.name, // Use parent question name for error
                  message: `Please fill details${missingDetailsStr} for ${
                    question.label || question.name
                  }`,
                  questionPath: currentPath.join(" > "),
                  uid: question.uid || question.name,
                  categoryName: categoryInfo.categoryName,
                  subCategoryName: categoryInfo.subCategoryName,
                });
              }
            }
          }
        }
        // 2. Recurse into sub-questions
        if (question.sub_questions) {
          validateRecursively(
            question.sub_questions,
            currentPath,
            categoryInfo
          );
        }
      });
    };

    // Traverse categories
    if (categories && Array.isArray(categories)) {
      categories.forEach((category) => {
        if (category.questions) {
          validateRecursively(category.questions, [category.category_label], {
            categoryName: category.category_name,
            subCategoryName: null,
          });
        }
        if (category.sub_categories) {
          category.sub_categories.forEach((subCat) => {
            if (subCat.questions) {
              validateRecursively(
                subCat.questions,
                [category.category_label, subCat.category_label],
                {
                  categoryName: category.category_name,
                  subCategoryName: subCat.category_name,
                }
              );
            }
          });
        }
      });
    }

    return errors;
  }
  static normalizeUID(str) {
    if (!str) return str;
    const s = String(str);
    return s.toLowerCase().replace(/-/g, "").replace(/\n/g, "");
  }
}
